"use client";

import Link from "next/link";
import { useEffect, useRef } from "react";
import IngLogo from "@/components/brand/IngLogo";

const ING_TITLE = "ING-aanbiedingen en veelgestelde vragen";

const LINK_CLASS =
  "flex items-center gap-3 rounded-lg px-3 py-2.5 min-h-[44px] transition-colors hover:bg-blue-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0A8DE9]";

function Column({ title, children }) {
  return (
    <div>
      <p className="px-3 pb-2 text-[13px] text-gray-500">{title}</p>
      <ul className="m-0 list-none p-0">{children}</ul>
    </div>
  );
}

function ItemLink({ item, onClose }) {
  return (
    <li>
      <Link href={item.href} onClick={onClose} className={LINK_CLASS}>
        {item.icon ? <span className="text-[#0A8DE9]">{item.icon}</span> : null}
        <span className="flex min-w-0 flex-col leading-tight">
          <span className="text-[15px] font-semibold text-gray-900">{item.label}</span>
          {item.subtitle ? (
            <span className="text-[12px] text-gray-500">{item.subtitle}</span>
          ) : null}
        </span>
      </Link>
    </li>
  );
}

/**
 * Wat een klik op de knop "Aanbiedingen" doet. `open` is of het paneel open
 * staat (ook door hover), `vast` of het met een klik is vastgezet.
 * - dicht: opent en zet vast;
 * - open door hover (niet vast): blijft open en wordt vastgezet;
 * - vastgezet: sluit (en het vastzetten vervalt).
 */
export function aanbiedingenNaKlik({ open, vast }) {
  if (open && vast) return { open: false, vast: false };
  return { open: true, vast: true };
}

/**
 * Breed uitklappaneel onder de kopbalk (desktop, xl en breder), geopend door de
 * knop "Aanbiedingen". Het paneel zelf is `absolute top-full` onder de
 * `fixed` <nav>, dus de ouder moet de <nav> zijn.
 *
 * Sluiten met Escape (focus terug naar `triggerRef`) en met een klik buiten
 * het paneel en buiten `triggerRef` regelt dit component zelf zolang `open`.
 */
export default function NavAanbiedingenMenu({
  open,
  id,
  titels = {},
  soortReis = [],
  bestemmingen = [],
  handig = [],
  marketKey,
  ingHref = "/ing",
  onClose,
  triggerRef,
  onMouseEnter,
  onMouseLeave,
}) {
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    function onKeyDown(event) {
      if (event.key !== "Escape") return;
      onClose?.();
      triggerRef?.current?.focus();
    }
    function onMouseDown(event) {
      const target = event.target;
      if (panelRef.current?.contains(target)) return;
      if (triggerRef?.current?.contains(target)) return;
      onClose?.();
    }
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onMouseDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onMouseDown);
    };
  }, [open, onClose, triggerRef]);

  if (!open) return null;

  const showIng = marketKey === "nl-nl";
  const showHandig = handig.length > 0 || showIng;

  return (
    <div
      id={id}
      ref={panelRef}
      data-testid="nav-aanbiedingen-panel"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className="absolute left-0 top-full z-50 w-full border-b border-gray-200 bg-white shadow-lg"
    >
      <div
        className="mx-auto grid max-w-[1320px] gap-x-6 gap-y-6 px-4 py-6"
        style={{ gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}
      >
        {soortReis.length > 0 && (
          <Column title={titels.soortReis}>
            {soortReis.map((item) => (
              <ItemLink key={item.href} item={item} onClose={onClose} />
            ))}
          </Column>
        )}
        {bestemmingen.length > 0 && (
          <Column title={titels.bestemmingen}>
            {bestemmingen.map((item) => (
              <ItemLink key={item.href} item={item} onClose={onClose} />
            ))}
          </Column>
        )}
        {showHandig && (
          <Column title={titels.handig}>
            {handig.map((item) => (
              <ItemLink key={item.href} item={item} onClose={onClose} />
            ))}
            {showIng && (
              <li>
                <Link
                  href={ingHref}
                  onClick={onClose}
                  className={LINK_CLASS}
                  title={ING_TITLE}
                  aria-label={ING_TITLE}
                >
                  <IngLogo height={16} />
                  <span className="text-[15px] font-semibold text-gray-900">ING-aanbiedingen</span>
                </Link>
              </li>
            )}
          </Column>
        )}
      </div>
    </div>
  );
}
